import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ConcertCarousel = ({ title, concerts }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 500;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };

  return (
    <section className="py-8 md:py-12 bg-[#0a0a0a]">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl md:text-2xl font-semibold tracking-wide">
            {title}
          </h2>
          
          {/* Navigation Arrows */}
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canScrollLeft
                  ? 'border-white/30 text-white hover:border-white hover:bg-white/10'
                  : 'border-white/10 text-white/20 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canScrollRight
                  ? 'border-white/30 text-white hover:border-white hover:bg-white/10'
                  : 'border-white/10 text-white/20 cursor-not-allowed'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-5 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {concerts.map((concert) => (
            <div
              key={concert.id}
              onClick={() => navigate(`/content/${concert.id}`)}
              className="group relative flex-shrink-0 w-[450px] md:w-[550px] cursor-pointer"
            >
              <div className="relative h-[300px] md:h-[350px] rounded-lg overflow-hidden bg-[#1a1a1a]">
                {/* Image */}
                <img
                  src={concert.image}
                  alt={concert.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white text-xl md:text-2xl font-semibold mb-2 group-hover:text-[#e4002b] transition-colors">
                    {concert.title}
                  </h3>
                  <p className="text-white/80 text-sm mb-2">
                    {concert.description}
                  </p>
                  {concert.artists && (
                    <p className="text-white/60 text-xs mb-4 line-clamp-2">
                      with {concert.artists}
                    </p>
                  )}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/content/${concert.id}`);
                    }}
                    className="px-6 py-2.5 bg-[#e4002b] text-white text-xs font-medium tracking-wide rounded hover:bg-[#c70024] transition-colors"
                  >
                    {concert.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConcertCarousel;
